<template>
  <div
    class="vw-100 bg-dark header"
    id="header"
    data-component="header"
    :class="{ mobile: mobileLayout }"
  >
    <!-- do not remove the following line. This is where the spell checker takes the current locale from -->
    <span id="locale-for-spell-checker" style="display: none">{{
      currentLocaleCode
    }}</span>
    <b-navbar toggleable="md" :variant="headerVariant">
      <!-- dissalow home functionality for popupWindow -->
      <b-navbar-brand
        class="py-0 no-outline"
        @click="goHome()"
        :disabled="isPopupWindow"
      >
        <img
          id="ptc-logo"
          alt="PTC logo"
          src="@/assets/ptclogo.png"
          class="banner img-fluid"
          :class="{'clickable': !registrationRequired}"
        />
        <img
          id="skd-product-name"
          :alt="productNameInfo.alt"
          :src="productNameInfo.img"
          class="banner img-fluid"
          :class="{'clickable': !registrationRequired}"
        />
      </b-navbar-brand>
      <template v-if="loggedIn && !isPopupWindow">
        <b-navbar-toggle class="navbar-link" target="menu-collapse">
          <b-icon-justify></b-icon-justify>
        </b-navbar-toggle>
        <!--------- Not in Production Warning ----------->
        <span class="containertext-center" v-if="showNonProdMessage">
          {{ $t("case-management.warning.not-on-production") }}
        </span>
        <b-collapse id="menu-collapse" is-nav>
          <b-navbar-nav id="top-menu" class="ml-auto">
            <quest-menu-bundled
              v-if="loggedIn && isProjectOpen && fold && !hasFieldServiceSkin"
            ></quest-menu-bundled>
            <!------ Profile Menu ---------------->
            <profile-menu v-if="isProfileVisible" />
            <!------ Project Menu ---------------->
            <project-menu v-if="isProjectVisible && !xs" />
            <!-- don't display on mobile -->
            <!------ Service session worklist Menu ---------------->
            <service-session-work-list-menu
              v-if="isProjectOpen && hasFieldServiceSkin && dsEnabled"
            />
            <!------ Solution Search Menu ---------------->
            <solution-search
              v-if="isProjectOpen && hasFieldServiceSkin && dsEnabled"
            />
            <!------ Repair History Menu ---------------->
            <repair-history
              v-if="isProjectOpen && hasFieldServiceSkin && dsEnabled"
            />
            <!------ Language Menu ---------------->
            <language-menu />
            <!------ kmoffline download Menu ---------------->
            <b-nav-item
              @click.prevent="gotoKMOfflineDownload"
              v-b-tooltip.hover="$t('solo.download')"
              v-if="!isKMOffline && hasKMOfflineAccess && !xs && isNotOnRegisterPage"
            >
              <!-- don't display on mobile -->
              <b-icon-download variant="light" class="menu-icon" />
            </b-nav-item>
            <!------ kmoffline connectivity ---------------->
            <KMOfflineMenu v-if="isKMOffline"/>
            <!------ bookmark Menu ---------------->
            <template v-if="showBookmarkMenu && isNotOnRegisterPage">
              <bookmark-menu-sm v-if="mobileLayout" />
              <bookmark-menu v-else-if="desktopLayout" />
            </template>
            <!------ help Menu ---------------->
            <b-nav-item
              @click.prevent="showHelp"
              v-b-tooltip.hover="$t('header.help')"
            >
              <b-icon-question-circle variant="light" class="menu-icon" />
            </b-nav-item>
            <!------ Admin Menu ---------------->
            <b-nav-item
              v-if="showAdmin && isNotOnRegisterPage"
              v-b-tooltip.hover="$t('header.admin')"
              @click.prevent="getAdminInformation()"
            >
              <b-icon-gear variant="light" class="menu-icon" />
            </b-nav-item>
            <!------ user Menu ---------------->
            <user-menu v-if="isNotOnRegisterPage" />
          </b-navbar-nav>
        </b-collapse>
      </template>
    </b-navbar>
    <!-- standalone sub-header (quests). Will be hidden and included in the main menu on small screen  -->
    <!-- margin-top is to clear the header -->
    <b-col
      lg="12"
      v-if="loggedIn && isProjectOpen && !fold && !hasFieldServiceSkin"
    >
      <quest-menu-standalone />
    </b-col>
    <!-- margin-top is to clear the header -->
    <!-- <b-col v-else style="margin-top: 3rem" /> -->
    <!-- end right menu (logged-in) -->
  </div>
</template>

<script>
//---- components ---------
import BookmarkMenu from "./BookmarkMenu.vue";
import BookmarkMenuSm from "./BookmarkMenuSm.vue";
import KMOfflineMenu from "./KMOfflineMenu.vue";
import LanguageMenu from "./LanguageMenu.vue";
import ProfileMenu from "./ProfileMenu.vue";
import ProjectMenu from "./ProjectMenu.vue";
import QuestMenuBundled from "./QuestMenuBundled.vue";
import QuestMenuStandalone from "./QuestMenuStandalone.vue";
import RepairHistory from "./RepairHistoryMenu.vue";
import ServiceSessionWorkListMenu from "./ServiceSessionWorkListMenu.vue";
import SolutionSearch from "./SolutionSearchMenu.vue";
import UserMenu from "./UserMenu.vue";
//---- mixins -------------
import KmOfflineStatusMixin from "../../shared/KmOfflineStatusMixin.js"
import QuestMenuMixins from "./QuestMenuMixins.js";
//---- services -----------
import BrowserTabService from "@/app/services/browser-tab.service.js";
import SkinProperties from "@/app/common/skin-properties.js";
import ProjectHomeService from "@/app/services/projectHome.service.js";
import NotificationService from "@/app/services/notification.js";
//---- 3rd-parties --------
import { mapGetters } from "vuex";
import {
  BIconDownload,
  BIconGear,
  BIconJustify,
  BIconQuestionCircle,
} from "bootstrap-vue";

export default {
  name: "app-header",

  components: {
    BIconDownload,
    BIconGear,
    BIconJustify,
    BIconQuestionCircle,
    BookmarkMenu,
    BookmarkMenuSm,
    KMOfflineMenu,
    LanguageMenu,
    ProfileMenu,
    ProjectMenu,
    QuestMenuBundled,
    QuestMenuStandalone,
    RepairHistory,
    ServiceSessionWorkListMenu,
    SolutionSearch,
    UserMenu,
  },

  mixins: [KmOfflineStatusMixin, QuestMenuMixins],

  data() {
    return {
      message: "",
    };
  },

  created() {
    // handle page reload
    this.$store.dispatch("header/reload");
  }, // end created

  methods: {
    async getAdminInformation() {
      if (!(await this.checkForUnsavedAuthoringModifications())) return;
      this.safeRouterPush("/admin");
    },
    gotoKMOfflineDownload() {
      this.safeRouterPush("/kmofflineDownload");
    },
    async goHome() {
      if (this.registrationRequired)
        return;

      // if there are multiple project, go to project selection page
      if (this.projects && this.projects.length > 1) {
        return this.safeRouterPush("/project-selection");
      }

      if (this.isFieldServiceSkin) {
        // disable for now because there are lots of issues (see KAI-14098)
        return;
        // this.$root.$emit("close-all-ds-tabs", "");
        // return this.safeRouterPush("/ds/dashboard");
      }

      // reset by switching to the first quest
      if (this.currentProfileData?.quests) {
        await this.changeQuest(this.currentProfileData?.quests[0]);
        this.$root.$emit("set-active-tab", "");
      }
      await ProjectHomeService.callCenter();
    },
    // logout() {
    //   // window.localStorage.clear();
    //   window.sessionStorage.clear();
    //   this.$store.dispatch("reset");
    //   this.$store.dispatch("auth/logout").then(() => {
    //     this.safeRouterPush("/login");
    //   });
    // },
    showHelp(strUrl) {
      var helpWindow = null;
      if (typeof strUrl != "undefined")
        strUrl =
          "https://skd-knowledge.ptc.com/advisor/advisorHelp.jsp?case=K05405363";

      var x = (window.innerWidth - 800) / 2;
      x = x > 0 ? x : 10;

      var strHelpOptions = "location=no";
      strHelpOptions += ",toolbar=no";
      strHelpOptions += ",menubar=no";
      strHelpOptions += ",status=no";
      strHelpOptions += ",scrollbars=yes";
      strHelpOptions += ",resizable=yes";
      strHelpOptions += ",top=10";
      strHelpOptions += ",left=" + x;
      strHelpOptions += ",width=1005";
      strHelpOptions += ",height=650";

      if (helpWindow != null) {
        helpWindow.close();
        helpWindow = null;
      }
      helpWindow = window.open(strUrl, "advisor", strHelpOptions);

      if (helpWindow == null) {
        NotificationService.warn(this.$t("error.popup-blocker"));
        return;
      }
      helpWindow.focus();
    },
    async onHomeLinkCliked() {
      if (!(await this.checkForUnsavedAuthoringModifications(true))) return;
      ProjectHomeService.onHomeLinkClicked();
    },
  }, // end methods

  computed: {
    isNotOnRegisterPage() {
      return this.$route?.name != 'kmofflineRegister'
    },
    productNameInfo() {
      if (this.foldProductName) {
        if (this.isKMOffline)
          return { img: require('@/assets/skd-folded-offline-light.svg'),
                  alt: "skd offline" };
        else
          return { img: require('@/assets/skd-folded-light.svg'),
                   alt: "skd" };
      }
      else {
        if (this.isKMOffline)
          return { img: require('@/assets/skd-offline-light.svg'),
                   alt: "service knowledge and diagnostics offline" };
        else
          return { img: require('@/assets/skd-light.svg'),
                   alt: "service knowledge and diagnostics" };
      }
    },
    showNonProdMessage() {
      let branches = this.$store.getters["header/currentProject"]?.branches;
      if (!branches) 
        return false;
      const productionExists = branches.length == 3;
      if (!productionExists) 
        return false;

      // if production branch exists and production is not current branch
      return !branches[2].isCurrentBranch;
    },
    projects() {
      return this.$store.getters["header/projects"];
    },
    isFieldServiceSkin() {
      return this.$store.getters["header/currentSkin"] === "Field Service";
    }, // end isFieldServiceSkin()
    //checks if the project is open
    isProjectOpen() {
      return this.$store.getters["projectData/isProjectOpen"];
    },
    loggedIn() {
      return this.$store.getters["auth/loggedIn"];
    },
    user() {
      return this.$store.getters["header/userId"];
    },
    tools() {
      return this.$store.getters["header/tools"];
    },
    xs() {
      return this.$store.getters["xs"];
    },
    fold() {
      return this.$store.getters["viewportSize"].width < 992;
    },

    foldProductName() {
      if(this.isKMOffline){
      return this.$store.getters["viewportSize"].width < 850;
      }
      else{
      return this.$store.getters["viewportSize"].width < 420;
      }
    },

    isProfileVisible() {
      return this.$store.getters["projectData/profiles"]?.length > 1;
    },

    isProjectVisible() {
      let projects = this.$store.getters["header/projects"];
      if (projects.length > 1) return true;
      else return projects[0]?.branches.length > 1;
    },

    hasKMOfflineAccess() {
      return this.$store.getters["header/tools"].includes("KM Offline");
    },

    showAdmin() {
      return this.$store.getters["header/tools"].includes('Admin');
    },

    currentSkin() {
      return this.$store.getters["header/currentSkin"];
    },

    hasFieldServiceSkin() {
      return this.currentSkin == "Field Service";
    },

    // a popup window should have a menubar, but it should not be active
    isPopupWindow() {
      return BrowserTabService.isPopupWindow();
    },

    ...mapGetters({
      currentLocale: "header/currentLocale",
      isKMOffline: "kmOffline/isKMOffline",
      skinProperties: "projectData/skinProperties",
      hServiceConnected: "kmOffline/isConnected",
    }),

    currentLocaleCode() {
      return this.currentLocale?.code || "en";
    },

    showBookmarkMenu() {
      // don't show bookmark on project selection page
      if (this.$route.path.startsWith("/project-selection") || this.$route.path.startsWith('/kmoffline')) 
        return false;

      // for naming consistency reasons, the new property is now show-bookmark
      var showBookmark = SkinProperties.get(
        this.skinProperties,
        "show-bookmark",
        null
      );

      if (showBookmark != null) 
        return showBookmark;

      // still accept old name
      var showBookmarkDeprecated = SkinProperties.get(
        this.skinProperties,
        "showBookmark",
        null
      );
      if (showBookmarkDeprecated != null) 
        return showBookmarkDeprecated;

      return false; // default value (note if we put default value to true, the icon blinks shortly)
    },
    dsEnabled() {
      var showDsEnabled = SkinProperties.get(
        this.skinProperties,
        "dsEnabled",
        true
      );
      return showDsEnabled;
    },
  }, // end computed
};
</script>
<style scoped>
.containertext-center {
  width: 50%;
  color: #d19514 !important;
  text-align: center;
}

.navbar-link {
  color: #eeeeee !important;
  cursor: pointer;
}
nav {
  width: 100%;
}
span.skd {
  vertical-align: middle;
  font-size: 2rem;
  line-height: 30px;
}
.nav-item {
  padding-top: 5px;
  width: 42px;
  text-align: center;
}
.navbar-nav {
  flex-direction: row;
}
img.banner {
  height: 30px;
  margin-top: -6px;
}
#skd-product-name {
    margin-top: -5px;
}
.btn {
  margin-top: -7px;
}
.menu-line-1 {
  padding: 0px;
}
#menu-collapse >>> .menu-icon {
  font-size: 1.4em;
}
#menu-collapse >>> .nav-link {
  color: rgba(255, 255, 255, 0.5);
  padding: 0px 5px;
}
#menu-collapse >>> .nav-link:hover{
  color: rgba(255, 255, 255, 0.75);
}
#menu-collapse.show >>> #top-menu {
  margin-top: 0.5rem;
}
#top-menu {
  float: right;
}
#top-menu li {
  height: 2rem; /* this is required to prevent icon from covering part of the menu */
}
.no-outline {
  outline: 0;
}
.mobile {
  flex: 0 0 auto;
}
#header {
  z-index: 5;
  background-color: #10384f;
}
</style>
